// src/pages/Wallet/modals/TokenManagementModal.js
import React, { useState, useEffect, useContext } from 'react';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { WalletContext } from '../../../contexts/WalletContext';
import { useTranslation } from 'react-i18next';
import { ethers } from 'ethers';
import './TokenManagementModal.css';

// Simple ERC20 ABI with only the necessary functions to check token info
const ERC20_ABI = [
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function balanceOf(address) view returns (uint256)'
];

// SVG icons as components to maintain design consistency
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const RemoveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const TokenManagementModal = ({ 
  isOpen, 
  onClose, 
  tokens = [], 
  onToggleVisibility = () => {}, 
  onAddCustomToken = () => {},
  onRemoveCustomToken = () => {} 
}) => {
  const { t } = useTranslation();
  const { currentChain } = useContext(NetworkContext);
  const { currentWallet, provider } = useContext(WalletContext);
  
  // Form state
  const [tokenAddress, setTokenAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // UI state
  const [activeTab, setActiveTab] = useState('tokens'); // 'tokens' or 'add'
  
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTokenAddress('');
      setAddressError('');
      setTokenInfo(null);
      setActiveTab('tokens');
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  // Fetch token information when validating
  const fetchTokenInfo = async () => {
    if (!tokenAddress || !ethers.isAddress(tokenAddress)) {
      setAddressError(t('wallet.invalidTokenAddress'));
      return;
    }
    
    setIsLoading(true);
    setAddressError('');
    
    try {
      const contract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      
      // Fetch token details
      const [name, symbol, decimals, balance] = await Promise.all([
        contract.name(),
        contract.symbol(),
        contract.decimals(),
        contract.balanceOf(currentWallet.address)
      ]);
      
      setTokenInfo({
        address: tokenAddress,
        name,
        symbol,
        decimals: decimals.toString(),
        balance: balance.toString()
      });
      
    } catch (error) {
      console.error("Error fetching token info:", error);
      setAddressError(t('wallet.errorFetchingToken'));
      setTokenInfo(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handler for adding the custom token
  const handleAddToken = () => {
    if (tokenInfo) {
      onAddCustomToken(tokenInfo);
      setTokenAddress('');
      setTokenInfo(null);
      setActiveTab('tokens');
    }
  };
  
  // Handler for removing a custom token
  const handleRemoveToken = (address) => {
    if (address) {
      onRemoveCustomToken(address);
    }
  };
  
  // Toggle between tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTokenAddress('');
    setTokenInfo(null);
    setAddressError('');
  };
  
  return (
    <div className="token-management-overlay" onClick={onClose}>
      <div className="token-management-content" onClick={(e) => e.stopPropagation()}>
        <h2>{t('wallet.manageTokens')}</h2>
        
        {/* Tab navigation */}
        <div className="token-management-tabs">
          <button 
            className={`tab-btn ${activeTab === 'tokens' ? 'active' : ''}`}
            onClick={() => handleTabChange('tokens')}
          >
            {t('wallet.yourTokens')}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
            onClick={() => handleTabChange('add')}
          >
            {t('wallet.addToken')}
          </button>
        </div>
        
        {/* Token List Tab */}
        {activeTab === 'tokens' && (
          <div className="token-list-container">
            {tokens.length > 0 ? (
              tokens.map((token, index) => {
                // Skip native token in this list
                if (!token.token || token.token.type === "native") return null;
                
                // Check if this is a custom token (has isCustom flag)
                const isCustomToken = token.token.isCustom;
                
                return (
                  <div key={index} className="token-management-item">
                    <div className="token-info">
                      <span className="token-symbol">{token.token.symbol}</span>
                      <span className="token-name">{token.token.name}</span>
                    </div>
                    
                    <div className="token-actions">
                      {isCustomToken && (
                        <button 
                          className="remove-token-btn" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveToken(token.token.address);
                          }}
                          title={t('wallet.removeToken')}
                        >
                          <RemoveIcon />
                        </button>
                      )}
                      <label className="toggle-switch">
                        <input 
                          type="checkbox" 
                          checked={!token.hidden} 
                          onChange={() => onToggleVisibility(token.token.address)} 
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                );
              }).filter(Boolean)
            ) : (
              <p className="no-tokens-message">{t('tokenModal.noTokensFound')}</p>
            )}
          </div>
        )}
        
        {/* Add Token Tab */}
        {activeTab === 'add' && (
          <div className="add-token-container">
            <p className="add-token-description">
              {t('wallet.addTokenDescription')}
            </p>
            
            <div className="token-address-input-container">
              <label className="input-label">{t('wallet.tokenContractAddress')}</label>
              <div className="address-input-row">
                <input
                  type="text"
                  className={`token-address-input ${addressError ? 'input-error' : ''}`}
                  placeholder="0x..."
                  value={tokenAddress}
                  onChange={(e) => setTokenAddress(e.target.value)}
                />
                <button 
                  className="search-token-btn"
                  onClick={fetchTokenInfo}
                  disabled={isLoading || !tokenAddress}
                >
                  {isLoading ? t('wallet.searching') : (
                    <>
                      <SearchIcon />
                      <span>{t('wallet.search')}</span>
                    </>
                  )}
                </button>
              </div>
              {addressError && <div className="address-error">{addressError}</div>}
            </div>
            
            {tokenInfo && (
              <div className="token-info-container">
                <h3>{t('wallet.tokenFound')}</h3>
                <div className="token-info-grid">
                  <div className="info-row">
                    <span className="info-label">{t('wallet.tokenName')}</span>
                    <span className="info-value">{tokenInfo.name}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('wallet.tokenSymbol')}</span>
                    <span className="info-value">{tokenInfo.symbol}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">{t('wallet.tokenDecimals')}</span>
                    <span className="info-value">{tokenInfo.decimals}</span>
                  </div>
                </div>
                
                <button 
                  className="add-token-button"
                  onClick={handleAddToken}
                >
                  <CheckIcon />
                  <span>{t('wallet.addToWallet')}</span>
                </button>
              </div>
            )}
          </div>
        )}
        
        <button className="close-button-management" onClick={onClose}>
          {t('actions.close')}
        </button>
      </div>
    </div>
  );
};

export default TokenManagementModal;